import { useEffect, useRef } from 'react';

export interface SocketManagerInterface {
  disconnect(): void;
  emit(event: string, ...args: any[]): void;
  init(url: string, token: string, options: any): Promise<void>;
  isConnected(): boolean;
  off(event: string | string[]): void;
  on(event: string | string[], callback: (...args: any[]) => void): void;
  updateToken(token: string): void;
}

let workerInstance: null | Worker = null;

const _SocketManager = class implements SocketManagerInterface {
  private connected = false;
  private connecting = false;
  private eventQueue: Array<{ args: any[]; event: string }> = [];
  private listeners: Map<string, (...args: any[]) => void> = new Map();
  private worker: null | Worker = null;

  private handleMessage(event: MessageEvent) {
    const data = event.data;
    if (!data || typeof data !== 'object') {
      return;
    }
    let { args = [], message, payload, type } = data;
    if (message !== undefined && !args.length) {
      args = [message];
    }
    if (payload && typeof payload === 'object') {
      args = [payload, ...args];
    }
    if (typeof type !== 'string') return;
    if (type === 'connect') {
      this.connected = true;
      this.connecting = false;
    } else if (type === 'disconnect') {
      this.connected = false;
      this.connecting = false;
    }
    const callback = this.listeners.get(type);
    if (callback) {
      try {
        callback(...args);
      } catch (error) {
        console.error(`Manager: Error in listener for event ${type}:`, error);
      }
    }
    if (type === 'connect_error') {
      this.connected = false;
      this.connecting = false;
      const errArg = args[0];
      const errorText = typeof errArg === 'string' ? errArg : errArg?.message || '';
      if (errorText.includes('auth')) {
        console.warn('Manager: Socket auth error - token may need refresh');
      }
      console.error('Manager: Connection error:', errorText);
    }
  }

  disconnect() {
    this.connected = false;
    this.connecting = false;
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }

  emit(event: string, ...args: any[]) {
    if (this.worker && this.connected) {
      this.worker.postMessage({
        payload: { args, event },
        type: 'emit',
      });
    } else if (this.worker) {
      this.eventQueue.push({ args, event });
    } else {
      console.warn('Manager: Cannot emit - socket manager not initialized');
    }
  }

  async init(url: string, token: string, options: any): Promise<void> {
    if (typeof window === 'undefined') {
      throw new Error('Worker only available in browser environment');
    }
    if (this.connecting) {
      return;
    }
    this.connecting = true;
    try {
      if (!workerInstance) {
        workerInstance = new Worker('/socket-io.worker.js');
      }
      this.worker = workerInstance;
      if (!this.worker) {
        throw new Error('Manager: Failed to initialize Worker');
      }
      this.worker.onmessage = this.handleMessage.bind(this);

      this.worker.postMessage({
        payload: {
          options: {
            transports: ['websocket', 'polling'],
            ...options,
          },
          token,
          url,
        },
        type: 'connect',
      });
      this.on('connect', () => {
        while (this.eventQueue.length) {
          const { args, event } = this.eventQueue.shift()!;
          this.emit(event, ...args);
        }
      });
    } catch (error) {
      this.connecting = false;
      console.error('Manager: SocketManager init failed:', error);
      throw error;
    }
  }

  isConnected(): boolean {
    return this.connected;
  }

  off(event: string | string[]) {
    const events = Array.isArray(event) ? event : [event];
    events.forEach((ev) => {
      this.listeners.delete(ev);
      if (this.worker) {
        this.worker.postMessage({
          payload: { event: ev },
          type: 'off',
        });
      }
    });
  }

  on(event: string | string[], callback: (...args: any[]) => void) {
    const events = Array.isArray(event) ? event : [event];
    events.forEach((ev) => this.listeners.set(ev, callback));
  }

  updateToken(token: string) {
    if (this.worker) {
      this.worker.postMessage({
        payload: { token },
        type: 'update_token',
      });
    }
  }
};

export const socketManager: SocketManagerInterface = new _SocketManager();

export const useSocketManagerCleanup = () => {
  const workerRef = useRef<null | Worker>(null);
  useEffect(() => {
    workerRef.current = (socketManager as any).worker;
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, []);
};
